import { useTranslation } from "react-i18next";
import type { WidgetProps } from "@/plugins/dashboardWidgets";
import type { TicketListItem } from "@/lib/api";
import { humanDuration } from "@/lib/status";
import { cn } from "@/lib/cn";
import { ticketCustomer } from "@/components/agent/dashboard/ticketCustomer";
import { formatDurationSince } from "../legacy";
import { WidgetFrame, type FrameLink } from "../chrome/WidgetFrame";
import { useDrill } from "../drill/useDrill";
import { useWidgetData } from "../data";
import { WidgetDataBody, WidgetEmpty } from "../chrome/WidgetDataBody";
import { TicketLink } from "../chrome/TicketLink";

export interface EscalationsSettings {
  limit: number;
  /** 0 = only breached deadlines (today's strip); otherwise also those due within N hours. */
  within_hours: number;
}

export type EscalationKind = "first_response" | "update" | "solution" | "escalation";

export interface EscalationsData {
  items: { ticket: TicketListItem; epoch: number; kind: EscalationKind }[];
  total: number;
  oldest_epoch: number | null;
}

const KINDS: readonly string[] = ["first_response", "update", "solution", "escalation"];

/** Breached and upcoming deadlines, nearest first: kind · customer and the signed time. */
export function EscalationsWidget({ instanceId, settings, size, mode }: WidgetProps<EscalationsSettings>) {
  const { t } = useTranslation();
  const drill = useDrill(instanceId, mode);
  const { status, data } = useWidgetData<EscalationsData>();
  const total = status === "ok" && typeof data?.total === "number" ? data.total : 0;
  const board = mode === "wallboard";
  const link: FrameLink | undefined = drill && total > 0 ? { label: t("dashboards.state.showAll", { count: total }), ...drill() } : undefined;
  return (
    <WidgetFrame
      title={t("dashboards.widgets.escalations.title")}
      link={link}
      mode={mode}
      scaleBody={!board}
      testId="escalations-widget"
      actions={board ? undefined : <span className="shrink-0 text-xs text-muted">{t("dashboards.widgets.escalations.order")}</span>}
    >
      <WidgetDataBody<EscalationsData> h={size.h}>
        {(data) => {
          const items = Array.isArray(data?.items) ? data.items : [];
          if (items.length === 0)
            return (
              <WidgetEmpty>
                {settings.within_hours > 0
                  ? t("dashboards.widgets.escalations.emptyWithin", { count: settings.within_hours })
                  : t("dashboards.widgets.escalations.empty")}
              </WidgetEmpty>
            );
          const now = Date.now() / 1000;
          const navIds = items.map((x) => x.ticket.id);
          if (board && size.h <= 2) {
            // One banner with the next deadline (the server sorts nearest first).
            const { ticket, epoch } = items[0];
            const breached = epoch <= now;
            const customer = ticketCustomer(ticket).label;
            const what = ticket.title ? (customer ? `${ticket.title} · ${customer}` : ticket.title) : `#${ticket.tn}`;
            const when = breached
              ? t("dashboards.due.since", { duration: formatDurationSince(epoch) })
              : t("dashboards.due.in", { duration: humanDuration(t, epoch - now) });
            return (
              <p
                data-testid="escalation-banner"
                className="flex h-full min-w-0 items-center truncate rounded-[0.6em] border border-danger/30 bg-danger/10 px-[0.8em] text-[length:calc(22px*var(--wb-zoom,1))] font-medium text-danger"
              >
                <span className="truncate">{t("dashboards.widgets.escalations.nextDeadline", { what, when })}</span>
              </p>
            );
          }
          return (
            <ul className={cn("flex flex-col", board ? "gap-[0.6em] text-[length:calc(20px*var(--wb-zoom,1))]" : "gap-2")}>
              {items.map(({ ticket, epoch, kind }) => {
                const breached = epoch <= now;
                const customer = ticketCustomer(ticket).label;
                const kindLabel = t(`dashboards.widgets.escalations.kind.${KINDS.includes(kind) ? kind : "escalation"}`);
                return (
                  <li key={ticket.id}>
                    <TicketLink
                      ticketId={ticket.id}
                      navIds={navIds}
                      mode={mode}
                      testId={`escalation-${ticket.id}`}
                      className="flex items-center gap-3 rounded-[10px] bg-surface-subtle px-3 py-2.5 text-ink transition-colors hover:bg-accent-dim focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
                    >
                      <span
                        aria-hidden="true"
                        className={cn("w-1 self-stretch rounded-sm", breached ? "bg-danger" : "bg-escalation")}
                      />
                      <span className="min-w-0 flex-1">
                        <span className={cn("block truncate font-medium", board ? "text-[1em]" : "text-sm")}>{ticket.title}</span>
                        <span className={cn("block truncate text-muted", board ? "text-[0.8em]" : "text-xs")}>
                          {customer ? `${kindLabel} · ${customer}` : kindLabel}
                        </span>
                      </span>
                      <span
                        className={cn(
                          "shrink-0 font-mono font-semibold tabular-nums", board ? "text-[0.8em]" : "text-xs",
                          breached ? "text-danger" : "text-escalation",
                        )}
                      >
                        {breached
                          ? t("dashboards.due.since", { duration: formatDurationSince(epoch) })
                          : t("dashboards.widgets.escalations.remaining", { duration: humanDuration(t, epoch - now) })}
                      </span>
                    </TicketLink>
                  </li>
                );
              })}
            </ul>
          );
        }}
      </WidgetDataBody>
    </WidgetFrame>
  );
}
